import { Stack } from "@fluentui/react/lib/Stack"
import { IProcessedStyleSet } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React, { useEffect, useState } from "react"

import { ContainerTO } from "@encoway/c-services-js-client"
import {
    ComponentFactory,
    Constants,
    StateEvent
} from "@encoway/react-configurator"

import { ComponentName } from "../../constants"
import { HeaderStyles } from "./Header.styles"
import { HeaderProps, IHeaderStyles } from "./Header.types"

function findContainer(
    container: ContainerTO,
    query: (c: ContainerTO) => boolean
): ContainerTO | null {
    if (query(container)) {
        return container
    }
    for (const child of container.children) {
        const result: ContainerTO | null = findContainer(child, query)
        if (result) {
            return result
        }
    }
    return null
}

/**
 * The Header renders general product information including image, name, long-text and short-text.
 *
 * In addition the Header shows a dropdown menu button which contains global operations.
 *
 * @visibleName Header
 */
function IHeader(props: HeaderProps) {
    const { styles, theme, ...delegatedProps } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<IHeaderStyles>

    const [container, setContainer] = useState<ContainerTO | null>(null)

    useEffect(() => {
        const unregister = props.eventBus
            .filter(
                (e: StateEvent) =>
                    e.event === Constants.Events.InitialState ||
                    e.event === Constants.Events.UpdateState
            )
            .onValue(async (e: StateEvent) => {
                const newContainer = e.rawState
                if (!newContainer.translatedName) {
                    // Note: This is a workaround because the configuration service might miss name and translated name for
                    // product groups in CPQ Studio + k-build embedded scenarios.
                    // See CPQ-1740
                    console.debug("searching for product name in tree...")
                    const tree = await props.configurationService.linkedTree()
                    const cont = findContainer(
                        tree.rootContainer,
                        (c: ContainerTO) => c.id === e.id
                    )
                    if (cont) {
                        newContainer.translatedName = cont.translatedName
                        newContainer.name = cont.name
                    } else {
                        newContainer.translatedName = null
                        newContainer.name = null
                    }
                }
                setContainer(newContainer)
            })
        return () => unregister()
    }, [])

    if (!container) {
        return null
    }

    const price = ComponentFactory.instanceOf(ComponentName.ProductPrice, {
        key: "price",
        ...delegatedProps,
        container
    })

    const title = ComponentFactory.instanceOf(ComponentName.ProductTitle, {
        key: "title",
        ...delegatedProps,
        container
    })

    const hasNonWhitespaceCharacter = (string?: string) => {
        return string !== undefined && /\S/.test(string)
    }

    const hasText =
        hasNonWhitespaceCharacter(container.shortText) ||
        hasNonWhitespaceCharacter(container.longText)

    let text, center
    if (!hasText) {
        center = [title, price]
    } else {
        text = ComponentFactory.instanceOf(ComponentName.ProductText, {
            key: 1,
            ...delegatedProps,
            container
        })
        center = [text, price]
    }

    const menu = ComponentFactory.instanceOf(ComponentName.HeaderMenu, {
        ...delegatedProps
    })

    return (
        <Stack className={classNames.root}>
            <Stack
                className={classNames.header}
                tokens={{ childrenGap: "1em" }}
                horizontal
            >
                {hasText && <Stack className={classNames.west}>{title}</Stack>}
                <Stack
                    className={classNames.center}
                    tokens={{ childrenGap: "1em" }}
                    horizontal
                >
                    {center}
                </Stack>
                <Stack className={classNames.east}>{menu}</Stack>
            </Stack>
        </Stack>
    )
}

/**
 * The Header renders general product information including image, name, long-text and short-text.
 *
 * In addition the Header shows a dropdown menu button which contains global operations.
 *
 * @visibleName Header
 */
export const Header = styled(IHeader, HeaderStyles)
Header.displayName = "Header"
